import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { creditsService } from '../services/api';
import { Hostel } from '../types';
import HostelCreditsCell from './HostelCreditsCell';
import './LoadHostelCreditsModal.css';

type LoadHostelCreditsModalProps = {
  hostel: Hostel | null;
  onClose: () => void;
  onSuccess?: (credits: { emailCredits: number; whatsappCredits: number }) => void;
};

const LoadHostelCreditsModal: React.FC<LoadHostelCreditsModalProps> = ({
  hostel,
  onClose,
  onSuccess,
}) => {
  const [emailCredits, setEmailCredits] = useState(0);
  const [whatsappCredits, setWhatsappCredits] = useState(0);
  const [emailAdd, setEmailAdd] = useState('100');
  const [whatsappAdd, setWhatsappAdd] = useState('100');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hostel?.id) return;
    let cancelled = false;
    setLoading(true);
    void creditsService
      .getHostelCredits(hostel.id)
      .then((data) => {
        if (!cancelled) {
          setEmailCredits(data.emailCredits);
          setWhatsappCredits(data.whatsappCredits);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load current credits');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hostel?.id]);

  if (!hostel?.id) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = Number(emailAdd);
    const whatsapp = Number(whatsappAdd);
    if (
      (!Number.isFinite(email) || email < 0 || !Number.isFinite(whatsapp) || whatsapp < 0) ||
      (email <= 0 && whatsapp <= 0)
    ) {
      toast.error('Enter at least one amount greater than zero');
      return;
    }
    setSaving(true);
    try {
      const result = await creditsService.loadHostelCredits(hostel.id, {
        emailCreditsAdd: email,
        whatsappCreditsAdd: whatsapp,
      });
      setEmailCredits(result.emailCredits);
      setWhatsappCredits(result.whatsappCredits);
      toast.success(`Credits loaded for ${hostel.name}`);
      onSuccess?.(result);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not load credits');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="load-credits-title">
      <div className="modal-panel load-credits-modal">
        <div className="modal-header">
          <h2 id="load-credits-title">Load credits</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form className="modal-body load-credits-modal__body" onSubmit={(e) => void submit(e)}>
          <p className="load-credits-modal__hostel">
            <strong>{hostel.name}</strong>
            {hostel.hostelCode ? <span className="load-credits-modal__code">{hostel.hostelCode}</span> : null}
          </p>

          <div className="load-credits-modal__current">
            <span className="load-credits-modal__current-label">Current balance</span>
            {loading ? (
              <span className="load-credits-modal__loading">Loading…</span>
            ) : (
              <HostelCreditsCell emailCredits={emailCredits} whatsappCredits={whatsappCredits} />
            )}
          </div>

          <div className="load-credits-modal__grid">
            <label>
              Email credits to add
              <input
                type="number"
                min={0}
                value={emailAdd}
                onChange={(e) => setEmailAdd(e.target.value)}
                placeholder="0"
              />
              <span className="load-credits-modal__hint">Added to current email balance</span>
            </label>
            <label>
              WhatsApp credits to add
              <input
                type="number"
                min={0}
                value={whatsappAdd}
                onChange={(e) => setWhatsappAdd(e.target.value)}
                placeholder="0"
              />
              <span className="load-credits-modal__hint">Added to current WhatsApp balance</span>
            </label>
          </div>

          <div className="load-credits-modal__actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || loading}>
              {saving ? 'Loading…' : 'Load credits'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoadHostelCreditsModal;
